import { z } from "zod";

export enum Connector {
  Stripe = "stripe",
  GoogleAnalytics = "google-analytics",
  GoogleDrive = "google-drive",
  Zapier = "zapier",
  Gmail = "gmail",
  Notion = "notion",
}

export type ApiKeyConnector = Connector.Zapier;
export const apiKeyConnectors = [Connector.Zapier];

export const FindConnectionFilterSchema = z.object({
  connector: z.enum(Connector),
  includeRemoved: z.boolean().optional(),
});

const NangoConnectionSchema = z.object({
  nangoConnectionId: z.uuid(),
  nangoIntegrationId: z.string(),
});

const StripeConnectorConfigurationSchema = z.object({
  ...NangoConnectionSchema.shape,
  connector: z.literal(Connector.Stripe),
});

const GoogleAnalyticsConnectorConfigurationSchema = z.object({
  ...NangoConnectionSchema.shape,
  connector: z.literal(Connector.GoogleAnalytics),
  /**
   * The Google Analytics account the organization is connected to. Can be a website or an app.
   */
  connectedAccount: z
    .object({
      id: z.string(),
      name: z.string(),
      propertyName: z.string(),
      propertyDisplayName: z.string(),
    })
    .optional(),
});

const GoogleDriveConnectorConfigurationSchema = z.object({
  ...NangoConnectionSchema.shape,
  connector: z.literal(Connector.GoogleDrive),
});

const NotionConnectorConfigurationSchema = z.object({
  ...NangoConnectionSchema.shape,
  connector: z.literal(Connector.Notion),
});

const GmailConnectorConfigurationSchema = z.object({
  ...NangoConnectionSchema.shape,
  connector: z.literal(Connector.Gmail),
});

const ZapierConnectorConfigurationSchema = z.object({
  connector: z.literal(Connector.Zapier),
  apiKey: z.string(),
  maskedApiKey: z.string(),
});

/**
 * This is a discriminated union of all supported connector configurations.
 * Includes connectors that use Nango and connectors that use other auth methods (e.g. API keys with Zapier).
 */
export const ConnectorConfigurationSchema = z.discriminatedUnion("connector", [
  StripeConnectorConfigurationSchema,
  GoogleAnalyticsConnectorConfigurationSchema,
  GoogleDriveConnectorConfigurationSchema,
  GmailConnectorConfigurationSchema,
  ZapierConnectorConfigurationSchema,
  NotionConnectorConfigurationSchema,
]);

export type FindConnectionFilter = z.infer<typeof FindConnectionFilterSchema>;

export type ZapierConnectorConfiguration = z.infer<
  typeof ZapierConnectorConfigurationSchema
>;

export type ConnectorConfiguration = z.infer<
  typeof ConnectorConfigurationSchema
>;

export function isConnectorZapier(
  configuration?: ConnectorConfiguration,
): configuration is ZapierConnectorConfiguration {
  return configuration?.connector === Connector.Zapier;
}
